import React from 'react';
import { cn } from '@/lib/cn';
import { IconAlertTriangle, IconChevronRight } from '@repo/svgs';
import { MiniTag } from './MiniTag';
import type { MiniTagSeverity } from './MiniTag';
import { toSentenceCase } from '@/utils/textTransform';

export type ViolationCellState = 'default' | 'hover' | 'press';

export interface ViolationCellProps extends React.HTMLAttributes<HTMLDivElement> {
  count: number;
  title: string;
  description: string;
  severity?: MiniTagSeverity;
  /**
   * Show the leading alert icon. When false, the text is left-aligned.
   */
  leadingIcon?: boolean;
  /**
   * Custom icon to show instead of the default alert icon. Only used when leadingIcon is true.
   */
  icon?: React.ReactNode;
  /**
   * Show the trailing chevron. When false, layout keeps space for alignment.
   */
  trailingIcon?: boolean;
}

/**
 * ViolationCell
 * Matches the Figma violation cell patterns (with/without leading icon, with/without trailing icon).
 */
export const ViolationCell = React.forwardRef<HTMLDivElement, ViolationCellProps>(
  (
    {
      count,
      title,
      description,
      severity = 'medium',
      leadingIcon = false,
      icon,
      trailingIcon = true,
      className,
      ...props
    },
    ref,
  ) => {
    const isInteractive = trailingIcon;

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-start justify-between gap-3 rounded-xl px-2 py-3 transition-colors',
          'active:bg-primary-press',
          isInteractive && 'hover:bg-primary-hover cursor-pointer',
          !isInteractive && 'cursor-default',
          className,
        )}
        {...props}
      >
        <div className="flex flex-1 items-start gap-3 min-w-0">
          {leadingIcon && (
            <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
              {icon || <IconAlertTriangle className="w-5 h-5 icon-alert-mid" />}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 min-w-0">
              <span className="text-heading-6 text-primary shrink-0">{count}</span>
              <span className="text-body-2-high text-primary font-semibold truncate">{title}</span>
              <MiniTag severity={severity} className="shrink-0" />
            </div>
            <p className="text-body-2 text-secondary line-clamp-2">{toSentenceCase(description)}</p>
          </div>
        </div>
        {trailingIcon ? (
          <IconChevronRight className="w-3 h-3 icon-secondary mt-1 shrink-0" />
        ) : (
          <span className="w-3 h-3 mt-1 shrink-0 opacity-0" aria-hidden />
        )}
      </div>
    );
  },
);

ViolationCell.displayName = 'ViolationCell';

export default ViolationCell;
